import * as z from 'zod'

import { GetModelTuningResponse, UpdateModelTuningVars } from '@/common/api/params/site'

const parseJsonField = <T,>(field: unknown, fallback: T): T => {
  if (field === null || field === undefined) return fallback
  if (typeof field === 'object') return field as T
  if (typeof field === 'string') {
    try {
      return JSON.parse(field) as T
    } catch {
      return fallback
    }
  }
  return fallback
}

export const modelTuningSchema = z.object({
  hybrid_weights: z.object({
    collaborative: z.number().min(0).max(1),
    neural: z.number().min(0).max(1),
    content_based: z.number().min(0).max(1)
  }),
  collaborative: z.object({
    n_factors: z.number().int().min(5).max(200),
    svd_knn_ratio: z.number().min(0).max(1),
    knn_neighbors: z.number().int().min(5).max(100),
    action_scores: z.object({
      view: z.number().min(0).max(10),
      cart: z.number().min(0).max(10),
      purchase: z.number().min(0).max(10)
    })
  }),
  neural: z.object({
    embed_dim: z.number().int().min(8).max(128),
    mlp_layers: z.array(z.number().int().positive()),
    dropout: z.number().min(0).max(0.5),
    epochs: z.number().int().min(1).max(50),
    learning_rate: z.number().min(0.0001).max(0.01),
    batch_size: z.number().int().min(32).max(1024),
    neg_ratio: z.number().int().min(1).max(10)
  }),
  content_based: z.object({
    max_features: z.number().int().min(500).max(50000)
  })
})

export type ModelTuningFormSchemaType = z.infer<typeof modelTuningSchema>

export const DEFAULT_TUNING_SCHEMA: ModelTuningFormSchemaType = {
  hybrid_weights: { collaborative: 0.4, neural: 0.4, content_based: 0.2 },
  collaborative: {
    n_factors: 50,
    svd_knn_ratio: 0.6,
    knn_neighbors: 40,
    action_scores: { view: 1.0, cart: 3.0, purchase: 5.0 }
  },
  neural: {
    embed_dim: 32,
    mlp_layers: [128, 64, 32],
    dropout: 0.2,
    epochs: 10,
    learning_rate: 0.001,
    batch_size: 256,
    neg_ratio: 4
  },
  content_based: { max_features: 5000 }
}

export const convertTuningResponseToSchema = (
  data: GetModelTuningResponse
): ModelTuningFormSchemaType => {
  const def = DEFAULT_TUNING_SCHEMA
  const hw = parseJsonField(data.hybrid_weights, def.hybrid_weights) as Record<string, number>
  const co = parseJsonField(data.collaborative, def.collaborative) as Record<string, unknown>
  const ne = parseJsonField(data.neural, def.neural) as Record<string, unknown>
  const cb = parseJsonField(data.content_based, def.content_based) as Record<string, unknown>
  const coAs = (co.action_scores as Record<string, number>) ?? def.collaborative.action_scores

  return {
    hybrid_weights: {
      collaborative: hw.collaborative ?? def.hybrid_weights.collaborative,
      neural: hw.neural ?? def.hybrid_weights.neural,
      content_based: hw.content_based ?? def.hybrid_weights.content_based
    },
    collaborative: {
      n_factors: (co.n_factors as number) ?? def.collaborative.n_factors,
      svd_knn_ratio: (co.svd_knn_ratio as number) ?? def.collaborative.svd_knn_ratio,
      knn_neighbors: (co.knn_neighbors as number) ?? def.collaborative.knn_neighbors,
      action_scores: {
        view: coAs.view ?? def.collaborative.action_scores.view,
        cart: coAs.cart ?? def.collaborative.action_scores.cart,
        purchase: coAs.purchase ?? def.collaborative.action_scores.purchase
      }
    },
    neural: {
      embed_dim: (ne.embed_dim as number) ?? def.neural.embed_dim,
      mlp_layers: Array.isArray(ne.mlp_layers)
        ? (ne.mlp_layers as number[])
        : def.neural.mlp_layers,
      dropout: (ne.dropout as number) ?? def.neural.dropout,
      epochs: (ne.epochs as number) ?? def.neural.epochs,
      learning_rate: (ne.learning_rate as number) ?? def.neural.learning_rate,
      batch_size: (ne.batch_size as number) ?? def.neural.batch_size,
      neg_ratio: (ne.neg_ratio as number) ?? def.neural.neg_ratio
    },
    content_based: {
      max_features: (cb.max_features as number) ?? def.content_based.max_features
    }
  }
}

export const convertTuningSchemaToVars = (
  siteId: number,
  schema: ModelTuningFormSchemaType
): UpdateModelTuningVars => ({
  site_id: siteId,
  hybrid_weights: schema.hybrid_weights as unknown as Record<string, unknown>,
  collaborative: schema.collaborative as unknown as Record<string, unknown>,
  neural: schema.neural as unknown as Record<string, unknown>,
  content_based: schema.content_based as unknown as Record<string, unknown>
})

export const normalizeHybridWeights = (
  key: keyof ModelTuningFormSchemaType['hybrid_weights'],
  val: number,
  current: ModelTuningFormSchemaType['hybrid_weights']
): ModelTuningFormSchemaType['hybrid_weights'] => {
  const hw = { ...current, [key]: val }
  const others = (
    Object.keys(hw) as (keyof ModelTuningFormSchemaType['hybrid_weights'])[]
  ).filter((k) => k !== key)
  const remaining = 1 - val
  const otherSum = others.reduce((s, k) => s + hw[k], 0)
  if (otherSum > 0) {
    others.forEach((k) => {
      hw[k] = Math.round((hw[k] / otherSum) * remaining * 100) / 100
    })
  } else {
    others.forEach((k) => {
      hw[k] = Math.round((remaining / others.length) * 100) / 100
    })
  }
  const total = Object.values(hw).reduce((s, v) => s + v, 0)
  if (Math.abs(total - 1.0) > 0.001) {
    hw[others[0]] = Math.round((hw[others[0]] + (1.0 - total)) * 100) / 100
  }
  return hw
}
